import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import type React from 'react';

const widths = {
  xs: 'tw:max-w-xs',
  sm: 'tw:max-w-sm',
  md: 'tw:max-w-md',
  lg: 'tw:max-w-lg',
  xl: 'tw:max-w-xl',
  '2xl': 'tw:max-w-2xl',
  '3xl': 'tw:max-w-3xl',
  '4xl': 'tw:max-w-4xl',
  '5xl': 'tw:max-w-5xl',
  full: 'tw:max-w-full',
};

export function Drawer({
  width = 'lg',
  className,
  children,
  ...props
}: {
  width?: keyof typeof widths;
  className?: string;
  children: React.ReactNode;
} & Omit<Headless.DialogProps, 'as' | 'className'>) {
  return (
    <Headless.Dialog {...props}>
      <Headless.DialogBackdrop
        transition
        className="tw:fixed tw:inset-0 tw:bg-zinc-950/25 tw:transition tw:duration-300 tw:focus:outline-0 tw:data-[closed]:opacity-0 tw:data-[enter]:ease-out tw:data-[leave]:ease-in tw:dark:bg-zinc-950/50 tw:z-50"
      />

      <div className="tw:fixed tw:inset-0 tw:overflow-hidden tw:z-50">
        <div className="tw:absolute tw:inset-0 tw:overflow-hidden">
          <div className="tw:pointer-events-none tw:fixed tw:inset-y-0 tw:right-0 tw:flex tw:max-w-full tw:pl-10">
            <Headless.DialogPanel
              transition
              className={clsx(
                className,
                widths[width],
                'tw:pointer-events-auto tw:w-screen tw:flex tw:flex-col tw:bg-white tw:shadow-xl tw:ring-1 tw:ring-zinc-950/10 tw:dark:bg-zinc-900 tw:dark:ring-white/10',
                'tw:transition tw:duration-300 tw:will-change-transform tw:data-[closed]:translate-x-full tw:data-[enter]:ease-out tw:data-[leave]:ease-in'
              )}
            >
              {children}
            </Headless.DialogPanel>
          </div>
        </div>
      </div>
    </Headless.Dialog>
  );
}

type DrawerHeaderProps = {
  className?: string;
  children: React.ReactNode;
} & React.ComponentPropsWithoutRef<'div'>;

export function DrawerHeader({ className, children, ...props }: DrawerHeaderProps) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'tw:flex tw:items-center tw:justify-between tw:px-6 tw:py-4 tw:border-b tw:border-zinc-950/5 tw:dark:border-white/5 tw:text-zinc-900 tw:dark:text-zinc-300'
      )}
    >
      {children}
    </div>
  );
}

type DrawerCloseButtonProps = {
  className?: string;
  'aria-label'?: string;
} & Omit<Headless.CloseButtonProps, 'as' | 'className'>;

export function DrawerCloseButton({
  className,
  'aria-label': ariaLabel,
  ...props
}: DrawerCloseButtonProps) {
  return (
    <Headless.CloseButton
      {...props}
      aria-label={ariaLabel || 'Close drawer'}
      className={clsx(
        className,
        'tw:rounded-md tw:p-1.5 tw:text-zinc-400 tw:transition tw:hover:bg-zinc-100 tw:hover:text-zinc-500 tw:dark:hover:bg-zinc-800 tw:dark:hover:text-zinc-300'
      )}
    >
      <svg
        className="tw:h-6 tw:w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </Headless.CloseButton>
  );
}

type DrawerTitleProps = {
  className?: string;
} & Omit<Headless.DialogTitleProps, 'as' | 'className'>;

export function DrawerTitle({ className, ...props }: DrawerTitleProps) {
  return <Headless.DialogTitle {...props} className={className} />;
}

type DrawerBodyProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

export function DrawerBody({ className, ...props }: DrawerBodyProps) {
  return (
    <div {...props} className={clsx(className, 'tw:flex-1 tw:overflow-y-auto tw:px-6 tw:pb-4')} />
  );
}

type DrawerFooterProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

export function DrawerFooter({ className, ...props }: DrawerFooterProps) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'tw:flex tw:items-center tw:justify-end tw:gap-3 tw:px-6 tw:py-4 tw:border-t tw:border-zinc-950/5 tw:dark:border-white/5'
      )}
    />
  );
}
